import { Config } from '../config';
import { EventTypeIdentifier } from '../enums';
import { HttpCodes } from '../enums/httpCodes';
import { CreateFleet, DeviceHealth, EventType, Fleet, FleetEventTypePayload, LicensePlateType, UpdateFleet } from '../interfaces';
import { AuthService } from './auth.service';
import { HttpService } from './http.service';

const path = `${Config.API}/api/v1/fleets`;

const displayId = (item: EventType) => {
  // wanted order

  // Bus Lane - Parking Match
  // Bus Lane - Moving Match
  // Bus Lane - Match
  // Bus Stop - Match
  // DOUBLE_PARKED_MATCH
  if (item.identifier === EventTypeIdentifier.BUS_LANE_PARKING_MATCH) {
    return 1;
  }
  if (item.identifier === EventTypeIdentifier.BUS_LANE_MOVING_MATCH) {
    return 2;
  }
  if (item.identifier === EventTypeIdentifier.BUS_LANE_MATCH) {
    return 3;
  }
  if (item.identifier === EventTypeIdentifier.BUS_STOP_MATCH) {
    return 4;
  }
  if (item.identifier === EventTypeIdentifier.DOUBLE_PARKED_MATCH) {
    return 5;
  }
  return undefined;
};

const eventTypesSorting = (array: EventType[], withDisplayId = false) => {
  if (!withDisplayId) {
    return array.sort((a, b) => a.identifier.localeCompare(b.identifier));
  }

  return array
    .map((item: EventType) => ({
      ...item,
      displayId: displayId(item),
    }))
    .sort((a, b) => a.identifier.localeCompare(b.identifier))
    .sort((a, b) => {
      if (a.displayId === undefined) return 1;
      if (b.displayId === undefined) return -1;
      return a.displayId > b.displayId ? 1 : -1;
    });
};

export abstract class FleetService {
  public static getAll = (): Promise<Fleet[]> => {
    return HttpService.fetchAuthorizedFleetless(path).then((res) => {
      return res || [];
    });
  };

  public static getHealthConfigFilters = (): Promise<string[]> => {
    const url = `${Config.API}/api/v1/alert_configs/filters`;
    return HttpService.fetchAuthorized(url).then((res) => {
      const filters = res.filter_period ?? [];
      filters.sort((a: string, b: string) => {
        return a.localeCompare(b);
      });

      return filters;
    });
  };

  public static getHealthConfig = (): Promise<any> => {
    const url = `${Config.API}/api/v1/alert_configs`;
    return HttpService.fetchAuthorized(url).then((res) => res);
  };

  public static getJiraTicketsForDevices = (): Promise<
    Array<Pick<DeviceHealth, 'id' | 'serial_number' | 'vehicle_id' | 'vehicle_name' | 'route_name' | 'created_at' | 'jira_ticket'>>
  > => {
    const url = `${Config.API}/api/v2/devices_health/jira_tickets`;
    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  // Sub-endpoints split from getHealth for performance. Each returns the base device fields plus its own
  // section, keyed by `id`, so results can be merged back into the DeviceHealth shape. See useFleetHealthComposed.
  // These endpoints are contractually arrays; coerce so a non-array body (e.g. an unparseable/error response)
  // can't break the merge in useFleetHealthComposed.
  public static getHealthStatusesForDevices = (): Promise<Partial<DeviceHealth>[]> => {
    const url = `${Config.API}/api/v2/devices_health/statuses`;
    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  public static getBusFeedForDevices = (): Promise<Partial<DeviceHealth>[]> => {
    const url = `${Config.API}/api/v2/devices_health/bus_feed`;
    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  public static getOTADetailsForDevices = (): Promise<Partial<DeviceHealth>[]> => {
    const url = `${Config.API}/api/v2/devices_health/ota_details`;
    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  public static getVersioningForDevices = (): Promise<Partial<DeviceHealth>[]> => {
    const url = `${Config.API}/api/v2/devices_health/versioning`;
    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  public static getLastEventsForDevices = (): Promise<Partial<DeviceHealth>[]> => {
    const url = `${Config.API}/api/v2/devices_health/last_events`;
    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  public static getLastCheckInForDevices = (): Promise<Partial<DeviceHealth>[]> => {
    const url = `${Config.API}/api/v2/devices_health/last_check_in`;
    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  public static getCumulativeUptimeForDevices = (): Promise<Partial<DeviceHealth>[]> => {
    const url = `${Config.API}/api/v2/devices_health/cumulative_uptime`;
    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  public static getHealth = (): Promise<DeviceHealth[]> => {
    const url = `${Config.API}/api/v2/devices_health`;

    return HttpService.fetchAuthorized(url).then((res) => (Array.isArray(res) ? res : []));
  };

  // Uploads (or replaces) a bus stop's annotation PDF. Passing localErrorHandling=true so the caller
  // can surface the backend's problem-details message (e.g. "only PDF files are allowed") instead of
  // the generic error toast. Success returns { [bus_stop_id]: presignedURL }.
  public static uploadZonePDF = (busStopId: string, file: File): Promise<Record<string, string>> => {
    const formData = new FormData();
    formData.append('file', file);

    return HttpService.fetchAuthorized(
      `${Config.API}/api/v1/zone-pdfs/${encodeURIComponent(busStopId)}`,
      { method: 'POST', body: formData },
      undefined,
      true,
    );
  };

  public static requestHealthReportCSV = (internal = false): Promise<{ Report: string }> => {
    let url = `${Config.API}/api/v1/reports/csv/fleethealth`;

    if (internal) {
      url += '?group_scope=internal';
    }

    return HttpService.fetchAuthorized(url, { method: 'POST' }).then((res) => res);
  };

  public static update = (uuid: string, body: UpdateFleet): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${uuid}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static create = (body: CreateFleet): Promise<Fleet> => {
    // Use fetchAuthorizedFleetless to avoid adding fleet_id from context
    // (we're creating a new fleet, not operating on existing one)
    return HttpService.fetchAuthorizedFleetless(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  };

  public static uploadFleetLogo = (uuid: string, body: FormData): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${uuid}/logo`, {
      method: 'POST',
      body,
    }).then(
      (res) => Promise.resolve(res),
      (rej) => Promise.reject(rej),
    );
  };

  // We allow a 401 here to not log out people who belong to NO_ACCESS group
  public static get = (uuid: string): Promise<Fleet> => {
    return HttpService.fetchAuthorized(`${path}/${uuid}`, undefined, undefined, true).then(
      (res) => {
        return res;
      },
      (rej) => {
        if (rej.status === HttpCodes.UNAUTHORIZED) {
          return Promise.resolve({});
        }
        return rej;
      },
    );
  };

  public static getMyFleet = async (): Promise<Fleet> => {
    try {
      const fleetId = await AuthService.getUserFleetId();
      if (fleetId !== null) {
        return FleetService.get(fleetId);
      }
      return Promise.reject();
    } catch (error) {
      console.error(error);
      return Promise.reject();
    }
  };

  public static getAllFleetsEventTypes = (): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/fleets/event_types`, {
      method: 'GET',
    }).then((eventTypesFromResponse) => {
      return eventTypesSorting(eventTypesFromResponse) || [];
    });
  };

  public static getFleetEventTypes = (fleetUuid: string): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/fleets/${fleetUuid}/event_types`, {
      method: 'GET',
    }).then((eventTypesFromResponse) => {
      return eventTypesSorting(eventTypesFromResponse, true) || [];
    });
  };

  public static createFleetEventTypes = (fleetUuid: string, fleetEventTypesPayload: FleetEventTypePayload): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/fleets/${fleetUuid}/event_types`, {
      method: 'POST',
      body: JSON.stringify(fleetEventTypesPayload),
    }).then((eventTypesFromResponse) => {
      return eventTypesFromResponse || [];
    });
  };

  public static updateFleetEventTypes = (
    fleetUuid: string,
    eventTypeId: number,
    fleetEventTypesPayload: FleetEventTypePayload,
  ): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/fleets/${fleetUuid}/event_types/${eventTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(fleetEventTypesPayload),
    }).then((eventTypesFromResponse) => {
      return eventTypesFromResponse || [];
    });
  };

  public static deleteFleetEventTypes = (fleetUuid: string, eventTypeId: number): Promise<EventType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/fleets/${fleetUuid}/event_types/${eventTypeId}`, {
      method: 'DELETE',
    }).then((eventTypesFromResponse) => {
      return eventTypesFromResponse || [];
    });
  };

  public static getLicensePlateTypes = (fleetUuid: string): Promise<LicensePlateType[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/fleets/${fleetUuid}/license_plate_types`, {
      method: 'GET',
    }).then((res) => {
      return res || [];
    });
  };

  public static getBlockNames = (fleetUuid: string, withDirections = false): Promise<string[]> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/fleets/${fleetUuid}/block_names?with_directions=${withDirections}`, {
      method: 'GET',
    }).then((res) => {
      return res || [];
    });
  };

  public static getFullFleetData = (fleetUuid: string): Promise<Fleet> => {
    return HttpService.fetchAuthorized(`${path}/${fleetUuid}`, {
      cache: 'no-store',
    }).then((res) => res);
  };
}
